import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ShieldCheck } from "lucide-react";
import { Logo } from "./Logo";
import { COMPANY } from "../data";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#manifesto" },
  { label: "Reviews", href: "#reviews" },
  { label: "Areas", href: "#areas" },
];

export const Header = ({ onQuote }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="header-navigation"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#0a0a0c]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" className="shrink-0"><Logo /></a>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="font-mono-sx text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-[#f5641e]" />
            <span className="font-mono-sx text-[10px] uppercase tracking-widest text-zinc-300">Gas Safe</span>
          </div>
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            data-testid="header-call-btn"
            className="hidden sm:flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-white hover:border-[#f5641e]/60 transition-colors"
          >
            <Phone className="h-4 w-4 text-[#f5641e]" />
            {COMPANY.phone}
          </a>
          <button
            onClick={onQuote}
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-[#f5641e] px-5 py-2.5 text-sm font-bold text-[#0a0a0c] hover:bg-[#ff7a38] transition-colors"
          >
            Get a Quote
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-1"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#0a0a0c]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-lg font-bold text-white"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { setOpen(false); onQuote(); }}
                className="mt-2 rounded-full bg-[#f5641e] px-5 py-3 font-bold text-[#0a0a0c]"
              >
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
